import {
  useCallback,
  useEffect,
  useMemo
} from "react";
import { CircleHelp } from "lucide-react";

import {
  ActivityHost,
  type ActivityHostBridgeEvents,
  type ActivityHostCommand,
  type ActivityHostCompletionMetrics,
  type ActivityHostLevelError,
  type ActivityHostReadyEvent
} from "@/features/activity-host";
import type { CurriculumAudioPlayer } from "@/features/audio";
import {
  createEventBridge,
  type EventBridge
} from "@/game/core/event-bridge";
import { PrimaryCommandButton } from "@/ui";

import {
  createPictureCharacterAdapter,
  pictureCharacterHostActions,
  soundBubbleAdapter,
  soundBubbleHostActions,
  storySequenceAdapter,
  storySequenceHostActions,
  type PictureCharacterAction,
  type SoundBubbleAction,
  type StorySequenceAction
} from "./adapters";
import type { ChinesePlayerActivityAudioKind } from "./audio-tracks";
import {
  getChinesePlayerLevel,
  type ChinesePlayerLevel,
  type PictureCharacterMatchLevel,
  type SoundBubbleLevel,
  type StorySequenceLevel
} from "./levels";
import {
  PictureCharacterRenderer,
  SoundBubblesRenderer,
  StorySequenceRenderer
} from "./renderers";
import "./chinese-player.css";

export interface ChineseNarrationRequest {
  readonly audioPath: string;
  readonly caption: string;
  readonly kind: "instruction" | ChinesePlayerActivityAudioKind;
  readonly levelId: string;
}

export interface ChineseLevelPlayerProps {
  readonly audioPlayer?: CurriculumAudioPlayer;
  readonly levelId: string;
  readonly nowMs?: () => number;
  readonly onComplete?: (
    metrics: ActivityHostCompletionMetrics
  ) => void;
  readonly onExit?: () => void;
  readonly onHostCommand?: (command: ActivityHostCommand) => void;
  readonly onHostReady?: (event: ActivityHostReadyEvent) => void;
  readonly onLevelError?: (error: ActivityHostLevelError) => void;
  readonly onNarrationRequest: (
    request: ChineseNarrationRequest
  ) => void;
  readonly onReplay?: (levelId: string) => void;
  readonly reducedMotion?: boolean;
}

interface ResolvedPlayerProps
  extends Omit<ChineseLevelPlayerProps, "audioPlayer" | "levelId"> {
  readonly audioPlayer: CurriculumAudioPlayer;
  readonly level: ChinesePlayerLevel;
}

const UNAVAILABLE_CHINESE_AUDIO_PLAYER: CurriculumAudioPlayer = {
  activeVoiceId: null,
  isPlayingPath: () => false,
  playPath: async () => false,
  stopOwnedVoice: () => undefined
};

function createInstructionNarration(
  level: ChinesePlayerLevel
): ChineseNarrationRequest {
  return {
    audioPath: level.promptAudio,
    caption: level.instruction,
    kind: "instruction",
    levelId: level.id
  };
}

function createActivityNarration(
  level: ChinesePlayerLevel
): ChineseNarrationRequest {
  switch (level.activityType) {
    case "sound-bubbles":
      return {
        audioPath: level.payload.soundAudio,
        caption: level.payload.spokenText,
        kind: "sound-stimulus",
        levelId: level.id
      };
    case "picture-character-match":
      return createInstructionNarration(level);
    case "story-sequence":
      return {
        audioPath: level.payload.narrationAudio,
        caption: level.payload.narration,
        kind: "story-narration",
        levelId: level.id
      };
  }
}

function createFallbackCaption(request: ChineseNarrationRequest) {
  return {
    endMs: null,
    mode: "fallback",
    startMs: null,
    text: request.caption,
    trackId: request.audioPath
  } as const;
}

function useChineseHostBridge<TAction>(
  level: ChinesePlayerLevel,
  onNarrationRequest: ChineseLevelPlayerProps["onNarrationRequest"],
  onReplay: ChineseLevelPlayerProps["onReplay"],
  onHostCommand: ChineseLevelPlayerProps["onHostCommand"],
  onHostReady: ChineseLevelPlayerProps["onHostReady"]
): EventBridge<ActivityHostBridgeEvents<TAction>> {
  const bridge = useMemo(
    () => createEventBridge<ActivityHostBridgeEvents<TAction>>(),
    []
  );
  const instructionNarration = useMemo(
    () => createInstructionNarration(level),
    [level]
  );
  const activityNarration = useMemo(
    () => createActivityNarration(level),
    [level]
  );

  const requestNarration = useCallback((request: ChineseNarrationRequest) => {
    const fallbackCaption = createFallbackCaption(request);
    bridge.emit("caption", fallbackCaption);

    try {
      onNarrationRequest(request);
    } catch {
      bridge.emit("caption", fallbackCaption);
    }
  }, [bridge, onNarrationRequest]);

  useEffect(() => {
    const readyTimers = new Set<ReturnType<typeof setTimeout>>();
    let currentNarration = instructionNarration;

    const scheduleReady = (narration: ChineseNarrationRequest) => {
      const timer = setTimeout(() => {
        readyTimers.delete(timer);
        const readyEvent = {
          caption: createFallbackCaption(narration)
        } satisfies ActivityHostReadyEvent;
        bridge.emit("ready", readyEvent);
        onHostReady?.(readyEvent);
        requestNarration(narration);
      }, 0);

      readyTimers.add(timer);
    };

    const unsubscribe = bridge.subscribe("command", (command) => {
      onHostCommand?.(command);

      switch (command.type) {
        case "start":
          currentNarration = activityNarration;
          requestNarration(currentNarration);
          break;
        case "replay-narration":
          requestNarration(currentNarration);
          break;
        case "replay":
          onReplay?.(level.id);
          currentNarration = activityNarration;
          scheduleReady(currentNarration);
          break;
        case "retry-level":
          currentNarration = instructionNarration;
          scheduleReady(currentNarration);
          break;
        case "pause":
        case "reduced-motion-changed":
        case "request-hint":
        case "resume":
        case "retry":
          break;
      }
    });

    scheduleReady(instructionNarration);

    return () => {
      unsubscribe();
      for (const timer of readyTimers) {
        clearTimeout(timer);
      }
    };
  }, [
    activityNarration,
    bridge,
    instructionNarration,
    level.id,
    onReplay,
    onHostCommand,
    onHostReady,
    requestNarration
  ]);

  return bridge;
}

function SoundBubblePlayer({
  audioPlayer,
  level,
  nowMs,
  onComplete,
  onExit,
  onHostCommand,
  onHostReady,
  onLevelError,
  onNarrationRequest,
  onReplay,
  reducedMotion
}: ResolvedPlayerProps & { readonly level: SoundBubbleLevel }) {
  const bridge = useChineseHostBridge<SoundBubbleAction>(
    level,
    onNarrationRequest,
    onReplay,
    onHostCommand,
    onHostReady
  );

  return (
    <ActivityHost
      actions={soundBubbleHostActions}
      adapter={soundBubbleAdapter}
      bridge={bridge}
      instruction={{
        caption: level.instruction,
        title: level.instruction
      }}
      levelId={level.id}
      levelTitle={level.title}
      nowMs={nowMs}
      onComplete={onComplete}
      onExit={onExit}
      onLevelError={onLevelError}
      payload={level.payload}
      reducedMotion={reducedMotion}
      renderActivity={(context) => (
        <SoundBubblesRenderer
          audioPlayer={audioPlayer}
          context={context}
          difficulty={level.difficulty}
        />
      )}
    />
  );
}

function PictureCharacterPlayer({
  audioPlayer,
  level,
  nowMs,
  onComplete,
  onExit,
  onHostCommand,
  onHostReady,
  onLevelError,
  onNarrationRequest,
  onReplay,
  reducedMotion
}: ResolvedPlayerProps & {
  readonly level: PictureCharacterMatchLevel;
}) {
  const adapter = useMemo(
    () => createPictureCharacterAdapter(level),
    [level]
  );
  const bridge = useChineseHostBridge<PictureCharacterAction>(
    level,
    onNarrationRequest,
    onReplay,
    onHostCommand,
    onHostReady
  );

  return (
    <ActivityHost
      actions={pictureCharacterHostActions}
      adapter={adapter}
      bridge={bridge}
      instruction={{
        caption: level.instruction,
        title: level.instruction
      }}
      levelId={level.id}
      levelTitle={level.title}
      nowMs={nowMs}
      onComplete={onComplete}
      onExit={onExit}
      onLevelError={onLevelError}
      payload={level.payload}
      reducedMotion={reducedMotion}
      renderActivity={(context) => (
        <PictureCharacterRenderer
          audioPlayer={audioPlayer}
          context={context}
          difficulty={level.difficulty}
        />
      )}
    />
  );
}

function StorySequencePlayer({
  audioPlayer,
  level,
  nowMs,
  onComplete,
  onExit,
  onHostCommand,
  onHostReady,
  onLevelError,
  onNarrationRequest,
  onReplay,
  reducedMotion
}: ResolvedPlayerProps & { readonly level: StorySequenceLevel }) {
  const bridge = useChineseHostBridge<StorySequenceAction>(
    level,
    onNarrationRequest,
    onReplay,
    onHostCommand,
    onHostReady
  );

  return (
    <ActivityHost
      actions={storySequenceHostActions}
      adapter={storySequenceAdapter}
      bridge={bridge}
      instruction={{
        caption: level.instruction,
        title: level.instruction
      }}
      levelId={level.id}
      levelTitle={level.title}
      nowMs={nowMs}
      onComplete={onComplete}
      onExit={onExit}
      onLevelError={onLevelError}
      payload={level.payload}
      reducedMotion={reducedMotion}
      renderActivity={(context) => (
        <StorySequenceRenderer
          audioPlayer={audioPlayer}
          context={context}
          difficulty={level.difficulty}
        />
      )}
    />
  );
}

function MissingChineseLevel({
  onExit
}: Pick<ChineseLevelPlayerProps, "onExit">) {
  return (
    <section
      aria-labelledby="chinese-player-missing-title"
      className="chinese-player__missing"
      role="alert"
    >
      <CircleHelp aria-hidden="true" />
      <h1 id="chinese-player-missing-title">这一关还没准备好</h1>
      <p>可以先回到地图，稍后再来看看。</p>
      {onExit ? (
        <PrimaryCommandButton onClick={onExit}>
          返回地图
        </PrimaryCommandButton>
      ) : null}
    </section>
  );
}

export function ChineseLevelPlayer(props: ChineseLevelPlayerProps) {
  const level = getChinesePlayerLevel(props.levelId);

  if (level === undefined) {
    return <MissingChineseLevel onExit={props.onExit} />;
  }

  const resolvedProps: ResolvedPlayerProps = {
    ...props,
    audioPlayer: props.audioPlayer ?? UNAVAILABLE_CHINESE_AUDIO_PLAYER,
    level
  };

  switch (level.activityType) {
    case "sound-bubbles":
      return <SoundBubblePlayer {...resolvedProps} level={level} />;
    case "picture-character-match":
      return <PictureCharacterPlayer {...resolvedProps} level={level} />;
    case "story-sequence":
      return <StorySequencePlayer {...resolvedProps} level={level} />;
  }
}
